import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDisplayCalendarDay, formatDisplayDateRange } from '@/lib/date-format';
import { usePlanningProduction, usePlanningWeeks } from '@/hooks/use-planning';

export function PlanningProductionPage() {
  const { data: weeks = [], isLoading: weeksLoading } = usePlanningWeeks();
  const [selectedWeekId, setSelectedWeekId] = useState<string>('');

  useEffect(() => {
    if (weeks.length === 0) {
      setSelectedWeekId('');
      return;
    }

    const hasSelectedWeek = weeks.some((week) => week.id === selectedWeekId);
    if (!hasSelectedWeek) {
      setSelectedWeekId(weeks[0].id);
    }
  }, [selectedWeekId, setSelectedWeekId, weeks]);

  const {
    data: production,
    isLoading: productionLoading,
    isError: productionError,
  } = usePlanningProduction(selectedWeekId || undefined, !!selectedWeekId);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Production</h1>
        <p className="text-muted-foreground">
          Select a week to view production totals and missing worklogs.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Week selection</CardTitle>
        </CardHeader>
        <CardContent>
          {weeksLoading ? (
            <p className="text-sm text-muted-foreground">Loading weeks...</p>
          ) : weeks.length === 0 ? (
            <p className="text-sm text-muted-foreground">No planning weeks yet.</p>
          ) : (
            <select
              value={selectedWeekId}
              onChange={(event) => setSelectedWeekId(event.target.value)}
              className="h-10 rounded-md border border-border bg-background px-3 text-sm"
            >
              {weeks.map((week) => (
                <option key={week.id} value={week.id}>
                  {formatDisplayDateRange(week.startDate, week.endDate)}
                </option>
              ))}
            </select>
          )}
        </CardContent>
      </Card>

      {weeks.length > 0 && !selectedWeekId && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Select a week to load production data.
          </CardContent>
        </Card>
      )}

      {selectedWeekId && productionLoading && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Loading production overview...
          </CardContent>
        </Card>
      )}

      {selectedWeekId && productionError && (
        <Card>
          <CardContent className="py-8 text-sm text-destructive">
            Failed to load production overview.
          </CardContent>
        </Card>
      )}

      {selectedWeekId && production && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Total Mails</CardTitle>
              </CardHeader>
              <CardContent className="text-2xl font-semibold">{production.totalMails}</CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Agents</CardTitle>
              </CardHeader>
              <CardContent className="text-2xl font-semibold">{production.perAgent.length}</CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Missing Worklogs</CardTitle>
              </CardHeader>
              <CardContent className="text-2xl font-semibold">
                {production.missingLogs.length}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Per Agent</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {production.perAgent.length === 0 ? (
                  <p className="text-muted-foreground">No production data.</p>
                ) : (
                  production.perAgent.map((row) => (
                    <div key={row.agentId} className="flex items-center justify-between rounded border border-border px-2 py-1">
                      <span>{row.agentName}</span>
                      <span className="font-medium">{row.mailsProcessed}</span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Per Project</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {production.perProject.length === 0 ? (
                  <p className="text-muted-foreground">No production data.</p>
                ) : (
                  production.perProject.map((row) => (
                    <div key={row.projectId} className="flex items-center justify-between rounded border border-border px-2 py-1">
                      <span>{row.projectName}</span>
                      <span className="font-medium">{row.mailsProcessed}</span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Per List</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {production.perList.length === 0 ? (
                  <p className="text-muted-foreground">No production data.</p>
                ) : (
                  production.perList.map((row) => (
                    <div key={row.listId} className="flex items-center justify-between rounded border border-border px-2 py-1">
                      <span>{row.listName}</span>
                      <span className="font-medium">{row.mailsProcessed}</span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Per Day</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {production.perDay.length === 0 ? (
                  <p className="text-muted-foreground">No production data.</p>
                ) : (
                  production.perDay.map((row) => (
                    <div key={row.date} className="flex items-center justify-between rounded border border-border px-2 py-1">
                      <span>{formatDisplayCalendarDay(row.date)}</span>
                      <span className="font-medium">{row.mailsProcessed}</span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Missing Logs</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {production.missingLogs.length === 0 ? (
                <p className="text-muted-foreground">No missing logs.</p>
              ) : (
                production.missingLogs.map((item) => (
                  <div key={item.id} className="rounded border border-border px-2 py-1">
                    <p className="font-medium">
                      {item.agent?.publicName ?? item.agent?.displayName ?? item.agentId}
                    </p>
                    <p className="text-muted-foreground">
                      {formatDisplayCalendarDay(item.date)} - {item.project?.name ?? item.projectId} /{' '}
                      {item.list?.name ?? item.listId}
                    </p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
